import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { registerAuthTokenGetter } from '../shared/network/auth-token-provider'
import authApiClient, {
  setupAuthInterceptor,
} from '@/features/shared/data/axios.interceptor'

export const useCheckAuthToken = (): boolean => {
  const [isReady, setIsReady] = useState(false)
  const interceptorRef = useRef<number | null>(null)

  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getIdTokenClaims,
    getAccessTokenSilently,
  } = useAuth0()

  const getTargetUrl = useCallback((): string => {
    const params = new URLSearchParams(window.location.search)
    params.delete('code')
    params.delete('state')
    const search = params.toString()
    return window.location.pathname + (search ? `?${search}` : '')
  }, [])

  const handleAuthFailure = useCallback(async (): Promise<void> => {
    await loginWithRedirect({ appState: { returnTo: getTargetUrl() } })
  }, [loginWithRedirect, getTargetUrl])

  const getAuthToken = useCallback(async (): Promise<string> => {
    let claims = await getIdTokenClaims()

    if (claims?.exp && claims.exp < Date.now() / 1000 + 30) {
      await getAccessTokenSilently({ cacheMode: 'off' })
      claims = await getIdTokenClaims()
    }

    const raw = claims?.__raw
    if (typeof raw !== 'string' || raw.length === 0) {
      throw new Error('No ID Token found')
    }
    return raw
  }, [getIdTokenClaims, getAccessTokenSilently])

  // Register axios interceptor — ejects and re-registers when auth0 functions change.
  useEffect(() => {
    if (interceptorRef.current !== null) {
      authApiClient.interceptors.request.eject(interceptorRef.current)
    }
    interceptorRef.current = setupAuthInterceptor(
      getAuthToken,
      handleAuthFailure
    )

    return () => {
      if (interceptorRef.current !== null) {
        authApiClient.interceptors.request.eject(interceptorRef.current)
        interceptorRef.current = null
      }
    }
  }, [getAuthToken, handleAuthFailure])

  // Keep singleton registration for direct token usage (e.g. algolia.service.ts).
  useEffect(() => {
    registerAuthTokenGetter(async () => {
      try {
        return await getAuthToken()
      } catch (error) {
        console.error(error)
        await handleAuthFailure()
        throw new Error('Failed to get access token')
      }
    })
  }, [getAuthToken, handleAuthFailure])

  // Auth guard: redirect when unauthenticated, verify token and set isReady when authenticated.
  useEffect(() => {
    if (isLoading) return

    if (!isAuthenticated) {
      setIsReady(false)
      void handleAuthFailure()
      return
    }

    const checkToken = async (): Promise<void> => {
      try {
        await getAuthToken()
        setIsReady(true)
      } catch {
        setIsReady(false)
        await handleAuthFailure()
      }
    }

    void checkToken()
  }, [isAuthenticated, isLoading, getAuthToken, handleAuthFailure])

  return isReady
}
